'use client'

import React, { useMemo, useState } from 'react'
import isEmpty from 'lodash-es/isEmpty'

import {
  CategorySummaryData,
  type AffiliateTile as AffiliateTileType,
  type ResponseItem,
} from '@rewardops/utils'

import ItemCardSkeleton from '~/components/maple/components/item-card/skeleton'
import { AffiliateTile } from '~/components/maple/components/affiliate-tile/affiliate-tile'
import ItemCard from '~/components/maple/item-card'
import { useTranslation } from '~/app/i18n/client'

const DEFAULT_TILE_POSITION = 5

interface ItemPaginationProps {
  category?: CategorySummaryData | null
  items?: ResponseItem[]
  error: any
  loading: boolean
  itemsPerPage: number
  affiliateTile?: AffiliateTileType | null
}

const styling =
  'grid grid-cols-2 lg:grid-cols-3 md:grid-cols-3 auto-rows-fr lg:gap-5 md:gap-5 sm:gap-0 lg:border-none md:border-none border-t border-r'

const ItemPagination: React.FC<ItemPaginationProps> = ({
  items,
  error,
  itemsPerPage,
  loading,
  affiliateTile,
}: ItemPaginationProps) => {
  const { t } = useTranslation()
  const [tileDismissed, setTileDismissed] = useState(false)

  const tilePosition = affiliateTile?.position ?? DEFAULT_TILE_POSITION
  const showTile = affiliateTile && !tileDismissed

  const skeleton = useMemo(() => {
    return Array.from({ length: itemsPerPage }).map((_, i) => (
      <ItemCardSkeleton key={i} />
    ))
  }, [itemsPerPage])

  if (error) {
    return <>An error occurred</>
  }

  if (isEmpty(items)) {
    if (loading) {
      return <div className={styling}>{skeleton}</div>
    }
    return <div className="px-2 lg:px-0">{t('general.noItems')}</div>
  }

  return (
    <div className={styling}>
      {items?.map((item: ResponseItem, index: number) => (
        <React.Fragment key={item.id}>
          {showTile && index === tilePosition && (
            <AffiliateTile
              content={affiliateTile}
              onDismiss={() => setTileDismissed(true)}
            />
          )}
          <ItemCard
            item={item}
            position={index}
            listName="items-list"
          />
        </React.Fragment>
      ))}
      {showTile && items && tilePosition >= items.length && (
        <AffiliateTile
          content={affiliateTile}
          onDismiss={() => setTileDismissed(true)}
        />
      )}
    </div>
  )
}

export default ItemPagination
